import React, {
  useContext,
  useMemo,
  useCallback,
  lazy,
  Suspense,
  useEffect
} from "react"
import { Helmet } from "react-helmet"
import {
  Route,
  Switch,
  RouteChildrenProps,
  useLocation,
  useRouteMatch
} from "react-router-dom"
import { useSelector } from "react-redux"
import { createActorContext } from "@xstate/react3"
import { MainContentLazyLoader as loading } from "../../../../mp-core/components"
import { RootState } from "../../../../mp-core/redux/store"
import { User } from "../../../../mp-database"
import { Roles } from "../../../../mp-utils/generic-utils"
import { FeatureFlagContext } from "../../../../mp-core/FeatureFlagProvider"
import { FeatureFlagName } from "../../../../mp-utils/generic-utils/feature-flags/types"
import {
  StudyPlanBreadcrumbs,
  StudyPlanMachineListeners
} from "../../../components"

import createStudyPlanMachine from "../../../machines/studyPlan"
import { EocRouteParams } from "../../types"
import { Context, StudyPlanTargets } from "../../../machines/studyPlan/types"
import { ProgramsContext } from "../../../../mp-shared-components/ready-shared-components/components/Navs"
import { Box } from "@mui/material"
import { RouteParams } from "../../../../reporting/types"
import { REPORTING_ROUTE_MATCH } from "../../../../reporting/constants"
import { getWorkkeysReplaceTerm } from "../../../../reporting/helpers/workkeysReplaceTerms"

import { Program } from "../../../types"
import { programNameGetter } from "../../../helpers"
import StudyPlanSubjectContainer from "./StudyPlanSubjectContainer"

export const StudyPlansLanding = lazy(() => import("../StudyPlansLanding"))

export const StudyPlansOverviewPage = lazy(
  () => import("../StudyPlansOverview")
)

export const TeacherStudyPlanPage = lazy(() => import("../TeacherStudyPlan"))

export const ReadOnlyStudyPlanPage = lazy(() => import("../ReadOnlyStudyPlan"))

export const StudyPlanViewPage = lazy(() => import("../StudyPlanView"))

export const StudyPlanEditPage = lazy(() => import("../StudyPlanEdit"))

export const StudyPlanItemPreviewPage = lazy(
  () => import("../StudyPlanItemPreview")
)

export const ScoresByStudentPage = lazy(() => import("../ScoresByStudentPage"))

const studyPlanMachine = createStudyPlanMachine()
// TODO: Use actual types for state machines ReturnType<typeof createStudyPlanMachine>
export const GlobalMachineContext = createActorContext(
  studyPlanMachine,
  null
  // state => console.log("[study-plans]", state.value, state.event, state.context)
)

type Props = RouteChildrenProps & {
  filteredProgram?: string
  filteredCourseSubject?: string
  selectedTest?: string[]
  setTestFilter?: (test: string) => void
}

const StudyPlansInit: React.FC<Props> = ({
  filteredProgram,
  filteredCourseSubject,
  selectedTest,
  setTestFilter,
  ...props
}) => {
  // Determine if we should show read-only study plan (WorkKeys) or editable (Ready)
  const isReadOnly = import.meta.env.VITE_APPLICATION_ID === "workkeys"
  const StudyPlanComponent = isReadOnly
    ? ReadOnlyStudyPlanPage
    : TeacherStudyPlanPage

  // Handle routing: old & new navigation
  const match =
    useRouteMatch<RouteParams>({
      path: REPORTING_ROUTE_MATCH,
      exact: false
    }) ?? props.match
  const isOldNav = match.path.includes("eoc")
  const basePath = isOldNav
    ? "/eoc/:program/:module(study-plans)"
    : "/reporting/teaching-tools/study-plan"
  const matchParams = isOldNav
    ? (match.params as EocRouteParams)
    : (match.params as RouteParams)
  const subPage = matchParams.subpage
  const entityType = isOldNav
    ? undefined
    : (matchParams as RouteParams).entityType

  // Get program and courseSubject from filters state (new navigation)

  const { user } = useSelector((state: RootState) => ({
    user: state.users.testUser as User
  }))

  // Get feature flags for server-side control
  const { featureFlags } = useContext(FeatureFlagContext)

  const isEditableAdmin = useCallback(
    (user: User): boolean => {
      const featureFlagEnabled =
        featureFlags?.[FeatureFlagName.study_plan_edit_mode_workkeys]

      // Note: this admin is our default study plan that all plans are based off of. We need to allow edit to build a default study plan.
      const EDITABLE_ADMIN_EMAIL = "defaultplan-da@mp.com"
      const EDITABLE_ADMIN_ID = "6a3e91a0-823f-11ee-bc06-1116031ca059"

      return (
        featureFlagEnabled ||
        user.email === EDITABLE_ADMIN_EMAIL ||
        user.id === EDITABLE_ADMIN_ID
      )
    },
    [featureFlags]
  )

  // Determine component for District/School Admins based on WorkKeys + user
  const AdminComponent = useMemo(() => {
    if (!isReadOnly || isEditableAdmin(user)) {
      return StudyPlansOverviewPage
    }

    return ReadOnlyStudyPlanPage // WorkKeys default - read-only
  }, [isReadOnly, isEditableAdmin, user])

  const { currentProgram, currentProgramCourse, currentProgramSetter } =
    useContext(ProgramsContext)

  useEffect(() => {
    if (!isOldNav && filteredProgram) {
      currentProgramSetter(filteredProgram)
    }
  }, [isOldNav, filteredProgram])

  const [program, subject] = useMemo(() => {
    if (isOldNav) {
      const eocParams = props?.match?.params as EocRouteParams
      const subject = eocParams?.subject ?? null
      return [currentProgram?.toUpperCase() as Program, subject]
    } else {
      return [filteredProgram?.toUpperCase() as Program, filteredCourseSubject]
    }
  }, [
    isOldNav,
    currentProgram,
    filteredProgram,
    filteredCourseSubject,
    props?.match?.params
  ])

  const programDisplayName = programNameGetter(
    program,
    currentProgramCourse?.programInfo?.displayName
  )

  const { search } = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])

  const currentTarget = useMemo(() => {
    if (isOldNav) {
      // For admins without a subject, go to landing
      if (!subject) {
        if (user.role === "District Admin" || user.role === "School Admin") {
          return StudyPlanTargets.Landing
        }
        return ""
      }

      if (!subPage) {
        if (user.role === "District Admin" || user.role === "School Admin") {
          return StudyPlanTargets.Overview
        }

        return ""
      }

      if (subPage === "view") {
        return StudyPlanTargets.ReviewPlan
      }

      return ""
    } else {
      if (subPage === "view" && entityType === "recommended") {
        return StudyPlanTargets.ViewRecommended
      } else if (subject && subPage === "view") {
        return StudyPlanTargets.ReviewPlan
      } else if (subject || subPage === "settings") {
        if (user.role === "Teacher") {
          return StudyPlanTargets.TeacherView
        }
        if (user.role === "District Admin" || user.role === "School Admin") {
          return StudyPlanTargets.Overview
        }
      } else {
        if (user.role === "Teacher") {
          return StudyPlanTargets.TeacherView
        }
        if (user.role === "District Admin" || user.role === "School Admin") {
          return StudyPlanTargets.Landing
        }
      }

      return ""
    }
  }, [subject, subPage, user.role, isOldNav, entityType])

  const initialContext: Partial<Context> = useMemo(() => {
    return {
      user,
      program,
      subject,
      currentTarget,
      isOldNav,
      isReadOnly: isEditableAdmin(user) ? false : isReadOnly,
      ...(subject &&
        subject !== "settings" && {
          initialUrlSubject: isOldNav ? subject : null
        }),
      ...(subject === "settings" &&
        (user.role === "District Admin" || user.role === "School Admin") && {
          viewGlobalSettings: true
        }),
      ...(searchParams.get("classId") && {
        selectedEntity: {
          entityId: searchParams.get("classId"),
          entityName: searchParams.get("className")
        }
      })
    }
  }, [currentTarget, subject, searchParams, user, program, isOldNav])

  return (
    <>
      {program && (
        <Helmet>
          <title>
            Study Plans - {programDisplayName?.documentHead} -{" "}
            {getWorkkeysReplaceTerm("masteryprep", true)}
          </title>
        </Helmet>
      )}
      <GlobalMachineContext.Provider options={{ context: initialContext }}>
        <StudyPlanMachineListeners>
          {isOldNav && <StudyPlanBreadcrumbs user={user} />}
          <Box component="main" tabIndex={0}>
            <Switch>
              <Route
                exact
                path="/eoc/:program/:module"
                render={props => {
                  // This is only needed for the old navigation, remove once we no longer need to support it
                  if (user.role === Roles.Teacher) {
                    return (
                      <Suspense fallback={loading}>
                        <StudyPlanComponent {...props} />
                      </Suspense>
                    )
                  }

                  if (
                    user.role === Roles.District_Admin ||
                    user.role === Roles.School_Admin
                  ) {
                    return (
                      <Suspense fallback={loading}>
                        <StudyPlansLanding {...props} />
                      </Suspense>
                    )
                  }
                }}
              />
              {/* new vs old nav - nested routes under basePath */}
              <Route
                path={
                  isOldNav
                    ? `${basePath}/:subject`
                    : `${basePath}/:subpage?/:entityType?`
                }
                render={routeProps => (
                  <StudyPlanSubjectContainer
                    program={program}
                    subject={subject}
                    isOldNav={isOldNav}
                    {...routeProps}
                  >
                    <Switch>
                      {/* view page */}
                      <Route
                        path={
                          isOldNav
                            ? `${basePath}/:subject/view`
                            : `${basePath}/view/:entityType?`
                        }
                        render={props => (
                          <Suspense fallback={loading}>
                            <StudyPlanViewPage {...props} />
                          </Suspense>
                        )}
                      />
                      {/* edit page */}
                      <Route
                        path={
                          isOldNav
                            ? `${basePath}/:subject/edit`
                            : `${basePath}/edit`
                        }
                        render={props => (
                          <Suspense fallback={loading}>
                            <StudyPlanEditPage user={user} {...props} />
                          </Suspense>
                        )}
                      />
                      {/* preview page */}
                      <Route
                        path={
                          isOldNav
                            ? `${basePath}/:subject/preview`
                            : `${basePath}/preview`
                        }
                        render={props => (
                          <Suspense fallback={loading}>
                            <StudyPlanItemPreviewPage {...props} />
                          </Suspense>
                        )}
                      />
                      {/* scores by student page */}
                      {featureFlags?.[
                        FeatureFlagName.study_plan_scores_by_student_module
                      ] && (
                        <Route
                          path={
                            isOldNav
                              ? `${basePath}/:subject/scores`
                              : `${basePath}/scores`
                          }
                          render={props => (
                            <Suspense fallback={loading}>
                              <ScoresByStudentPage {...props} />
                            </Suspense>
                          )}
                        />
                      )}
                      {/* overview/landing depending on role */}
                      <Route
                        exact
                        path={
                          isOldNav
                            ? `${basePath}/:subject`
                            : `${basePath}/:subpage?/:entityType?`
                        }
                        render={props => {
                          if (user.role === Roles.Teacher) {
                            if (subject) {
                              return (
                                <Suspense fallback={loading}>
                                  <StudyPlanComponent {...props} />
                                </Suspense>
                              )
                            }
                            return (
                              <Suspense fallback={loading}>
                                <StudyPlanComponent {...props} />
                              </Suspense>
                            )
                          }

                          if (
                            user.role === Roles.District_Admin ||
                            user.role === Roles.School_Admin
                          ) {
                            if (subject) {
                              return (
                                <Suspense fallback={loading}>
                                  <AdminComponent {...props} />
                                </Suspense>
                              )
                            }

                            // For read-only (WorkKeys), show loading while auto-select completes
                            // This prevents flash of editable landing page
                            if (isReadOnly && program) {
                              return loading
                            }

                            return (
                              <Suspense fallback={loading}>
                                <StudyPlansLanding {...props} />
                              </Suspense>
                            )
                          }
                        }}
                      />
                    </Switch>
                  </StudyPlanSubjectContainer>
                )}
              />
            </Switch>
          </Box>
        </StudyPlanMachineListeners>
      </GlobalMachineContext.Provider>
    </>
  )
}

export default StudyPlansInit
